import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import React from 'react'
import Environment from '../env/env';
import { addOrUpdateJob, archiveJobs, setSocketState, updatePreferences, useOwnerUserId } from './Global';
import ChatMessage from '../modules/chat/models/ChatMessage';

type SocketFunc = {
    key: string
    onStart: (skt: HubConnection) => void
    onStop: (skt: HubConnection) => void
    onReconnect?: () => void
}

export default class SocketContext {
    public socket: HubConnection;
    // Built on first use, not at import: the connection URL is the hub the tab settled on in
    // Environment.resolveHubUrl, which has not run yet while modules are loading.
    private static _instance?: SocketContext;
    public static get instance(): SocketContext {
        if (!SocketContext._instance) SocketContext._instance = new SocketContext();
        return SocketContext._instance;
    }

    private socketFuncs: Array<SocketFunc> = []

    // Timestamp of when we last entered the "Connecting" state. Used by the
    // watchdog below to detect a connection that is stuck negotiating and never
    // resolves/rejects (seen on first launch in the native WebView).
    private connectingSince: number = Date.now()

    constructor() {

        const env = new Environment();
        //const [global] = useGlobal();
        this.socket = new HubConnectionBuilder()
            .configureLogging((env.isDevelopment ? LogLevel.Debug : LogLevel.None))
            .withUrl(env.hubUrl + '/livehub', {
                headers: {
                    'SecretKey': env.secretKey,
                    'Authorization': ""
                }
            })
            .build();

        this.socket.start()
            .then(this.onStart)
            .catch(e => {
                setSocketState(HubConnectionState.Disconnected);
            });

        this.socket.onclose(() => {
            setSocketState(HubConnectionState.Disconnected);
        });


        setInterval(() => {
            const now = Date.now();

            if (this.socket.state == HubConnectionState.Disconnected) {
                this.connectingSince = now;
                setSocketState(HubConnectionState.Connecting);
                this.socket.start()
                    .then(() => {
                        this.onStart()

                        for (var i = 0; i < this.socketFuncs.length; i++) {
                            var func = this.socketFuncs[i];
                            func.onReconnect?.();
                            //console.log("let's run onreconnect")
                        }

                    })
                    .catch(() => {
                        setSocketState(HubConnectionState.Disconnected);
                    })
            }
            else if (this.socket.state == HubConnectionState.Connecting) {
                // Watchdog: if start() neither resolved nor rejected within 15s the
                // connection is stuck. Force a stop so onclose -> Disconnected fires
                // and the branch above restarts it on the next tick.
                if (now - this.connectingSince > 15000) {
                    this.connectingSince = now;
                    this.socket.stop().catch(() => { });
                }
            }
            else if (this.socket.state == HubConnectionState.Connected) {
                this.connectingSince = now;
            }
        }, 2000);


    }


    private onStart = () => {


        setSocketState(HubConnectionState.Connected);

        for (var i = 0; i < this.socketFuncs.length; i++) {
            var func = this.socketFuncs[i];
            func.onStart(this.socket);
        }
    }


    public start = (onStart: (skt: HubConnection) => void, onStop: (skt: HubConnection) => void, onReconnect?: () => void) => {
        var key = this.generateKey();
        if (this.socket.state == HubConnectionState.Connected) {
            onStart(this.socket);
        }

        this.socketFuncs.push({
            key: key,
            onStart,
            onStop,
            onReconnect
        })

        return key;
    }

    public stop = (key: string) => {
        var func: SocketFunc | undefined;
        var funcIndex = 0;
        for (var i = 0; i < this.socketFuncs.length; i++) {
            if (this.socketFuncs[i].key == key) {
                func = this.socketFuncs[i];
                funcIndex = i;
                break;
            }
        }

        if (func && this.socket.state == HubConnectionState.Connected) {
            func.onStop(this.socket);
        }

        this.socketFuncs.splice(funcIndex, 1);
    }

    public get = (invoke: (skt: HubConnection) => void) => {
        invoke(this.socket);
    }

    private generateKey = () => {
        var result = '';
        var characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        var charactersLength = characters.length;
        for (var i = 0; i < 30; i++) {
            result += characters.charAt(Math.floor(Math.random() * charactersLength));
        }
        return result;
    }



}
